import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext';
import { PlanProvider } from './context/PlanContext';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { isOrgAdminUser } from './utils/orgRoles';

import PlanGate from './components/PlanGate';
import PlanErrorInterceptor from './components/PlanErrorInterceptor';
import { Toaster } from './components/ui/sonner';
import ToasterBoundary from './components/ToasterBoundary';
import RuntimeShell from './shell/RuntimeShell';
import ModuleHost from './shell/ModuleHost';
import { InboxManagerGate } from './components/inbox/InboxManagerGate';
import Login from './pages/auth/Login';
import ChangePassword from './pages/auth/ChangePassword';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import AccountDetail from './pages/AccountDetail';
import Contacts from './pages/Contacts';
import ContactDetail from './pages/ContactDetail';
import EventOutreachStudio from './pages/EventOutreachStudio';
import Pipeline from './pages/Pipeline';
import LeadPipeline from './pages/LeadPipeline';
import DealDetail from './pages/DealDetail';
import DealRegistration from './pages/DealRegistration';
import Proposals from './pages/Proposals';
import ProposalCreator from './pages/ProposalCreator';
import AIAssistant from './pages/AIAssistant';
import Activities from './pages/Activities';
import Communications from './pages/Communications';
import Reports from './pages/Reports';
// Settings.jsx and Admin.jsx are no longer imported — routes now point to
// individual page components under pages/settings/ and pages/admin/.
// The old /settings and /admin routes use SettingsRedirect / AdminRedirect
// for backward-compat ?tab= → route redirects.
import DealRooms from './pages/DealRooms';
import DealRoomDetail from './pages/DealRoomDetail';
import PublicDealRoom from './pages/PublicDealRoom';
// Admin.jsx no longer imported — routes point to individual admin page components.
// Settings sub-pages (Phase 4 — route migration)
import SettingsProfilePage from './pages/settings/SettingsProfilePage';
import SettingsVoiceCapturePage from './pages/settings/SettingsVoiceCapturePage';
import SettingsIntegrationsPage from './pages/settings/SettingsIntegrationsPage';
import SettingsAIAssistantPage from './pages/settings/SettingsAIAssistantPage';
import SettingsSecurityPage from './pages/settings/SettingsSecurityPage';
import SettingsTemplatesSignaturesPage from './pages/settings/SettingsTemplatesSignaturesPage';
// Admin sub-pages (Phase 5 — route migration)
import AdminOrganizationPage from './pages/admin/AdminOrganizationPage';
import AdminPendingApprovalsPage from './pages/admin/AdminPendingApprovalsPage';
import AdminCoachesPage from './pages/admin/AdminCoachesPage';
import AdminUserGroupsPage from './pages/admin/AdminUserGroupsPage';
import AdminDealRoomTemplatesPage from './pages/admin/AdminDealRoomTemplatesPage';
import AdminEmailTemplatesPage from './pages/admin/AdminEmailTemplatesPage';
import AdminSignaturesPage from './pages/admin/AdminSignaturesPage';
import AdminAISkillsPage from './pages/admin/AdminAISkillsPage';
import AdminMediaLibraryPage from './pages/admin/AdminMediaLibraryPage';
import AdminVoiceProfilesPage from './pages/admin/AdminVoiceProfilesPage';
import AdminTagsPage from './pages/admin/AdminTagsPage';
import AdminDeveloperApiPage from './pages/admin/AdminDeveloperApiPage';
import AdminDeveloperApiKeyPage from './pages/admin/AdminDeveloperApiKeyPage';
import AdminCustomFieldsPage from './pages/admin/AdminCustomFieldsPage';
import AdminLeadPipelinePage from './pages/admin/AdminLeadPipelinePage';
import AdminNotificationsPage from './pages/admin/AdminNotificationsPage';
import AdminPipelinesPage from './pages/admin/AdminPipelinesPage';
import AdminEventTypesPage from './pages/admin/AdminEventTypesPage';
import AdminProductSettingsPage from './pages/admin/AdminProductSettingsPage';
import AdminAppLauncherPage from './pages/admin/AdminAppLauncherPage';
import AdminAIUsagePage from './pages/admin/AdminAIUsagePage';
import AdminUnifiedInboxPage from './pages/admin/AdminUnifiedInboxPage';
import AdminPitchBookSyncPage from './pages/admin/AdminPitchBookSyncPage';
import AdminPlanUsagePage from './pages/admin/AdminPlanUsagePage';
import AdminModulesPage from './pages/admin/AdminModulesPage';
import AdminDuplicateContactsPage from './pages/admin/AdminDuplicateContactsPage';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import EmailSequences from './pages/EmailSequences';
import SequenceEditor from './pages/SequenceEditor';
import SequenceMonitor from './pages/SequenceMonitor';
import ListManagement from './pages/ListManagement';
import Inbox from './pages/Inbox';
import InboxSettingsPage from './pages/InboxSettingsPage';
import EmailDetailPage from './components/inbox/EmailDetailPage';
import ComposePage from './components/inbox/ComposePage';
import PersonalThreadView from './components/inbox/PersonalThreadView';
import PersonalComposePage from './components/inbox/PersonalComposePage';
import DemoLogin from './pages/auth/DemoLogin';
import AcceptInvitePage from './pages/auth/AcceptInvitePage';
import SSOComplete from './pages/auth/SSOComplete';
import PendingApproval from './pages/auth/PendingApproval';
import VerifyEmail from './pages/auth/VerifyEmail';
import ResetPassword from './pages/auth/ResetPassword';
import ForgotPassword from './pages/auth/ForgotPassword';
import PublicBooking from './pages/PublicBooking';
import PublicBookingManage from './pages/PublicBookingManage';
import Forms from './pages/Forms';
import FormDataPage from './pages/FormDataPage';
import FormBuilder from './pages/FormBuilder';
import WorkflowCanvas from './pages/WorkflowCanvas';
import CustomPageDesigner from './pages/CustomPageDesigner';
import ForwarderPage from './pages/ForwarderPage';
import ConditionEditor from './pages/ConditionEditor';
import CalendarPage from './pages/Calendar';
import CalendarEventPage from './pages/calendar/CalendarEventPage';
import CalendarEditorPage from './pages/calendar/CalendarEditorPage';
import BookingLinksPage from './pages/BookingLinks';
import PublicForm from './pages/PublicForm';
import PublicReschedule from './pages/PublicReschedule';
import PublicUnsubscribe from './pages/PublicUnsubscribe';
import AIHeartbeat from './pages/AIHeartbeat';
import FeatureRequests from './pages/FeatureRequests';
import Campaigns from './pages/Campaigns';
import CampaignBuilder from './pages/CampaignBuilder';
import CampaignExecutionReport from './pages/CampaignExecutionReport';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import Onboarding from './pages/Onboarding';
import ReleaseNotes from './pages/ReleaseNotes';
import ContentLibrary from './pages/ContentLibrary';
import Tasks from './pages/Tasks';
import WikiV2 from './pages/WikiV2';
import Testimonials from './pages/Testimonials';
import HelpGuide from './pages/HelpGuide';
import TestimonialBuilder from './pages/TestimonialBuilder';
import PublicTestimonial from './pages/PublicTestimonial';
import PublicTestimonialView from './pages/PublicTestimonialView';
import TestimonialWall from './pages/TestimonialWall';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import AIPolicy from './pages/legal/AIPolicy';
import DataPolicy from './pages/legal/DataPolicy';
import TermsAndConditions from './pages/legal/TermsAndConditions';
import Subprocessors from './pages/legal/Subprocessors';
import GoogleLimitedUse from './pages/legal/GoogleLimitedUse';
import AgenticReviews from './pages/AgenticReviews';
import Agents from './pages/Agents';
import AgentBuilder from './pages/AgentBuilder';
import PlaybookList from './pages/PlaybookList';
import MeetingSessions from './pages/MeetingSessions';
import DesktopApp from './pages/DesktopApp';
// Practice / Role Play (v2.16 redesign)
import AvatarLibrary from './pages/admin/AvatarLibrary';
import ATSDashboard from './pages/ats/ATSDashboard';
import ATSJobDetail from './pages/ats/ATSJobDetail';
import ATSTalentPool from './pages/ats/ATSTalentPool';
import ATSAgentQueue from './pages/ats/ATSAgentQueue';
import ATSCVReview from './pages/ats/ATSCVReview';
import ATSApplicantDetail from './pages/ats/ATSApplicantDetail';
import ATSSettings from './pages/ats/ATSSettings';
import ATSReports from './pages/ats/ATSReports';
import ATSActivationCampaigns from './pages/ats/ATSActivationCampaigns';
import ATSMatchingWorkspace from './pages/ats/ATSMatchingWorkspace';
import CareersPage from './pages/public/CareersPage';
import JobDetailPage from './pages/public/JobDetailPage';
import ApplicantPortal from './pages/public/ApplicantPortal';
import ClientPortal from './pages/public/ClientPortal';
import { promptForgeTicket } from './lib/featureRequests';
import StarFeatureModal from './components/StarFeatureModal';
import { ProviderShell } from './components/modules/ProviderShell';
import { ProviderErrorBoundary } from './components/modules/ProviderErrorBoundary';
import { bindTaskQueries } from './lib/tasksApi';
import { capturePage } from './lib/reportImages';
// Hidden 2026-04-23 — AI Assistant orb + MiniChat needs a rework pass
// (see BACKLOG AIO-01). Leaving the import + render commented so the
// codepath comes back in one edit when we pick it up.
// import FloatingAI from './components/ai-entity/FloatingAI';
import { PromptForgeProvider, defaultSubmit } from '@multiplai_tech/prompt-forge';
import '@multiplai_tech/prompt-forge/theme.css';
import './App.css';

// Prompt Forge: ⌘⇧P opens modal, ⌥-click pins any element and generates
// a structured Claude Code prompt. Mounted for every authenticated user,
// gated to the `allowedMCPUsers` whitelist for direct-send to Claude.
//
// Screenshot capture: the report dialog's own capture (#932): html2canvas-pro
// loaded on first use, iframes and the report UI left out, a 15 s timeout, and
// a JPEG fitted under the 5 MB the server accepts (#931). A full-resolution PNG
// could exceed it, and the server then refused the whole ticket. A failed
// capture is non-fatal: Prompt Forge submits without the attachment.
const capturePageScreenshot = () => capturePage();

// Route `fr` / `bug` submissions to the existing /api/feature-requests
// endpoint, fall back to the package's defaultSubmit for `mcp` / `draft`.
// Returns { kind, id, label, url } so the modal's success state links back
// to the created ticket.
async function handlePromptForgeSubmit(payload, target) {
    if (target === 'fr' || target === 'bug') {
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const token = localStorage.getItem('crm_token');
        // Derive a reasonable title from the first markdown heading in the
        // generated prompt; fallback to the page id + mode so we always send
        // something that passes the >=5-char validation.
        const firstHeading = (payload.prompt || '').split('\n').find(l => l.startsWith('#'));
        const derivedTitle = (firstHeading || '')
            .replace(/^#+\s*/, '')
            .trim()
            .slice(0, 120);
        const fallback = `Prompt Forge — ${payload.pageId || 'unknown'} (${target})`;
        const title = derivedTitle.length >= 5 ? derivedTitle : fallback;

        const res = await fetch(`${API}/feature-requests/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
                type: target === 'bug' ? 'bug' : 'feature',
                title,
                description: payload.prompt || '',
                page_url: typeof window !== 'undefined' ? window.location.href : '',
                attachments: payload.withScreenshot && payload.screenshot ? [payload.screenshot] : [],
            }),
        });
        if (!res.ok) {
            const detail = await res.text().catch(() => '');
            throw new Error(`Feature request submit failed (${res.status}): ${detail}`);
        }
        // The answer is the control plane's row (camelCase `ticketNumber`, `id`).
        return promptForgeTicket(await res.json(), target, typeof window !== 'undefined' ? window.location.origin : '');
    }
    return defaultSubmit(payload, target, { mcpEndpoint: '/api/claude-mcp' });
}

function PromptForgeBridge({ children }) {
    const { user } = useAuth();
    // Render children without the provider when there's no user yet —
    // the bridge can't supply a valid reporter identity, and the login /
    // landing pages don't need the hotkey anyway.
    if (!user?.email) return children;
    return (
        <PromptForgeProvider
            user={{ email: user.email, name: user.name || user.email }}
            githubRepo="multiplai-tech/ai-crm-36"
            mcpEndpoint="/api/claude-mcp"
            allowedMCPUsers={['matt@aicoaches.com', 'matt@multiplai.tech', 'matt@kesby.land']}
            defaultMode="inferred"
            captureScreenshot={capturePageScreenshot}
            onSubmit={handlePromptForgeSubmit}
        >
            {children}
        </PromptForgeProvider>
    );
}

class ErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { hasError: false, error: null }; }
    static getDerivedStateFromError(error) { return { hasError: true, error }; }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: 40, maxWidth: 600, margin: '0 auto' }}>
                    <h2 style={{ color: '#dc2626', marginBottom: 8 }}>Something went wrong</h2>
                    <pre style={{ background: '#fef2f2', padding: 16, borderRadius: 8, fontSize: 12, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                        {this.state.error?.message || 'Unknown error'}
                        {'\n\n'}
                        {this.state.error?.stack?.split('\n').slice(0, 5).join('\n')}
                    </pre>
                    <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '8px 16px', background: '#1c1917', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                        Reload Page
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

function ReconnectingScreen() {
    useEffect(() => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const interval = setInterval(async () => {
            try {
                const res = await fetch(`${backendUrl}/api/health`, { method: 'GET', cache: 'no-store' });
                if (res.ok) window.location.reload();
            } catch (_) { /* still down */ }
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center">
            <div className="text-center max-w-sm">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h2 className="text-lg font-semibold text-stone-800 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Updating with new features...</h2>
                <p className="text-sm text-stone-500 mb-2">The system is being updated. This usually takes less than a minute.</p>
                <p className="text-xs text-stone-400 mb-4">The page will automatically reload when ready.</p>
                <div className="w-8 h-8 border-3 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 text-sm font-medium text-stone-600 bg-stone-100 rounded-lg hover:bg-stone-200"
                >
                    Retry Now
                </button>
            </div>
        </div>
    );
}

function ProtectedRoute({ children }) {
    const { user, loading, token, pendingPasswordChange } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500 text-sm">Loading CRM...</p>
                </div>
            </div>
        );
    }

    if (pendingPasswordChange) {
        return <Navigate to="/change-password" state={{ from: location }} replace />;
    }

    // Token exists but user is null → backend was temporarily unavailable (e.g. deploy in progress)
    // Show a retry screen instead of kicking to login
    if (!user && (token || localStorage.getItem('crm_token'))) {
        return <ReconnectingScreen />;
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Pending approval users can only see the pending page
    if (user.approval_status === 'pending' && location.pathname !== '/pending-approval') {
        return <Navigate to="/pending-approval" replace />;
    }
    return <RuntimeShell><ErrorBoundary>{children}</ErrorBoundary></RuntimeShell>;
}

/**
 * Issue #662 — guard for the manager-reachable per-inbox settings route
 * (/inbox/:inboxId/settings).
 *
 * InboxManagerGate composes ProtectedRoute (auth + its pending/reconnecting
 * branches) with ONE scoped permission check from the #663 permission store.
 * The order is load-bearing: the check NEVER decides while the store is
 * unknown. A manager landing directly on /inbox/:id/settings next day (cold
 * load) must see the page, never a redirect — deciding on the store's
 * fail-closed default would bounce legitimately entitled users (the same
 * failure mode WorkspaceContext documents for the sidebar). Unknown waits; a
 * resolved "no" redirects to /inbox. The gate lives in its own module
 * (components/inbox/InboxManagerGate.js) so the real component can be tested
 * against the real permission store (QA F-002).
 */
function InboxManagerRoute({ children }) {
    // Auth first (ProtectedRoute owns login/reconnecting/pending branches),
    // THEN the scoped permission gate. A logged-out visitor gets the login
    // redirect, not a permission spinner; a logged-in manager on a cold load
    // waits on permissionsUnknown rather than bouncing off the fail-closed
    // default.
    return (
        <ProtectedRoute>
            <InboxManagerGate>{children}</InboxManagerGate>
        </ProtectedRoute>
    );
}

/**
 * Route-level plan gate.
 *
 * The nav hid entitlement-gated AI pages; the router did not, so a bookmark or
 * a typed URL rendered the page anyway and it then failed one request at a time
 * — or, for `agentDeveloper` items, rendered fully to anyone authenticated,
 * because that restriction existed only in the sidebar.
 *
 * `PlanGate` supplies the upgrade prompt (and its own loading and
 * connection-error states), so this only has to compose it with the auth guard.
 */
function FeatureRoute({ feature, admin, children }) {
    const Guard = admin ? AdminRoute : ProtectedRoute;
    return (
        <Guard>
            <PlanGate feature={feature}>{children}</PlanGate>
        </Guard>
    );
}

function AdminRoute({ children }) {
    const { user, loading, token, pendingPasswordChange } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500 text-sm">Loading CRM...</p>
                </div>
            </div>
        );
    }

    if (pendingPasswordChange) {
        return <Navigate to="/change-password" state={{ from: location }} replace />;
    }

    if (!user && (token || localStorage.getItem('crm_token'))) {
        return <ReconnectingScreen />;
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!isOrgAdminUser(user)) {
        return <Navigate to="/dashboard" replace />;
    }

    return <RuntimeShell><ErrorBoundary>{children}</ErrorBoundary></RuntimeShell>;
}



/**
 * ModulesPage — route-level wrapper for the ProviderShell.
 *
 * Reads the optional `:providerId` route param and the `useAuth()` user
 * context, then renders ProviderShell inside a ProviderErrorBoundary.
 * When `:providerId` is present, ProviderShell opens focused on that
 * provider; otherwise it shows the full provider list.
 */
function ModulesPage() {
    const { providerId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    return (
        <ProviderErrorBoundary
            providerId={providerId || 'modules'}
            providerName={providerId ? `Module ${providerId}` : 'Modules'}
        >
            <ProviderShell
                activeProviderId={providerId || undefined}
                onNavigate={(path) => navigate(path)}
            />
        </ProviderErrorBoundary>
    );
}

// Backward-compat redirect: /settings?tab=<key> → /settings/<route> or /admin/<route>
function SettingsRedirect() {
    const [searchParams] = useSearchParams();
    const tab = searchParams.get('tab');
    const tabToRoute = {
        profile: '/settings/profile',
        voice: '/settings/voice-capture',
        integrations: '/settings/integrations',
        'ai-visual': '/settings/ai-assistant',
        security: '/settings/security',
        // These moved to admin
        organization: '/admin/organization',
        plan: '/admin/plan-usage',
        modules: '/admin/modules',
    };
    const target = (tab && tabToRoute[tab]) || '/settings/profile';
    return <Navigate to={target} replace />;
}

// Backward-compat redirect: /admin?tab=<key> → /admin/<route>
function AdminRedirect() {
    const [searchParams] = useSearchParams();
    const tab = searchParams.get('tab');
    const tabToRoute = {
        team: '/admin/organization', // Team Members → Organization
        pending: '/admin/pending-approvals',
        coaches: '/admin/coaches',
        groups: '/admin/user-groups',
        templates: '/admin/deal-room-templates',
        'email-templates': '/admin/email-templates',
        signatures: '/admin/signatures',
        skills: '/admin/ai-skills',
        media: '/admin/media-library',
        voice: '/admin/voice-profiles',
        tags: '/admin/tags',
        'custom-fields': '/admin/custom-fields',
        'lead-pipeline': '/admin/lead-pipeline',
        'event-types': '/admin/event-types',
        'product-settings': '/admin/product-settings',
        'app-launcher': '/admin/app-launcher',
        'ai-usage': '/admin/ai-usage',
        'unified-inbox': '/admin/unified-inbox',
        'pitchbook-sync': '/admin/pitchbook-sync',
        organization: '/admin/organization',
        plan: '/admin/plan-usage',
        modules: '/admin/modules',
    };
    const target = (tab && tabToRoute[tab]) || '/admin/organization';
    return <Navigate to={target} replace />;
}


function AppRouter() {
    return (
        <Routes>
            {/* Root redirects to login — landing page is now on the control plane */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/demo" element={<DemoLogin />} />
            <Route path="/join" element={<AcceptInvitePage />} />
            <Route path="/sso/complete" element={<SSOComplete />} />
            <Route path="/pending-approval" element={<PendingApproval />} />
            <Route path="/room/:slug" element={<PublicDealRoom />} />
            <Route path="/book/manage/:token" element={<PublicBookingManage />} />
            <Route path="/book/:slug" element={<PublicBooking />} />
            <Route path="/form/:slug" element={<PublicForm />} />
            <Route path="/reschedule/:bookingId" element={<PublicReschedule />} />
            <Route path="/unsubscribe" element={<PublicUnsubscribe />} />
            <Route path="/testimonial/:slug/:token" element={<PublicTestimonial />} />
            <Route path="/testimonial/view/:submissionId" element={<PublicTestimonialView />} />
            <Route path="/testimonials/wall/:orgSlug" element={<TestimonialWall />} />
            <Route path="/careers/:orgSlug" element={<CareersPage />} />
            <Route path="/careers/:orgSlug/jobs/:jobId" element={<JobDetailPage />} />
            <Route path="/applicant-portal" element={<ApplicantPortal />} />
            <Route path="/client-portal" element={<ClientPortal />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/ai-policy" element={<AIPolicy />} />
            <Route path="/data-policy" element={<DataPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            {/* Same six paths the apex serves — one path means one document on
                every host. Bodies come from content/legal/*.md via
                legal-content.generated.js; see pages/legal/LegalDocument.jsx. */}
            <Route path="/subprocessors" element={<Subprocessors />} />
            <Route path="/google-limited-use" element={<GoogleLimitedUse />} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/companies" element={<ProtectedRoute><Accounts /></ProtectedRoute>} />
            <Route path="/companies/:accountId" element={<ProtectedRoute><AccountDetail /></ProtectedRoute>} />
            <Route path="/accounts" element={<Navigate to="/companies" replace />} />
            <Route path="/accounts/:accountId" element={<ProtectedRoute><AccountDetail /></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
            <Route path="/contacts/:contactId" element={<ProtectedRoute><ContactDetail /></ProtectedRoute>} />
            <Route path="/contacts/:contactId/event-outreach" element={<ProtectedRoute><EventOutreachStudio /></ProtectedRoute>} />
            <Route path="/pipeline" element={<ProtectedRoute><Pipeline /></ProtectedRoute>} />
            <Route path="/lead-pipeline" element={<ProtectedRoute><LeadPipeline /></ProtectedRoute>} />
            <Route path="/deals/:dealId" element={<ProtectedRoute><DealDetail /></ProtectedRoute>} />
            <Route path="/deal-registration" element={<ProtectedRoute><DealRegistration /></ProtectedRoute>} />
            <Route path="/deal-rooms" element={<ProtectedRoute><DealRooms /></ProtectedRoute>} />
            <Route path="/deal-rooms/:roomId" element={<ProtectedRoute><DealRoomDetail /></ProtectedRoute>} />
            <Route path="/proposals" element={<ProtectedRoute><Proposals /></ProtectedRoute>} />
            <Route path="/proposals/new" element={<ProtectedRoute><ProposalCreator /></ProtectedRoute>} />
            <Route path="/proposals/:proposalId" element={<ProtectedRoute><ProposalCreator /></ProtectedRoute>} />
            <Route path="/ai-assistant" element={<FeatureRoute feature="ai_assistant"><AIAssistant /></FeatureRoute>} />
            <Route path="/activities" element={<ProtectedRoute><Activities /></ProtectedRoute>} />
            <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
            {/* Personal inbox full-page routes — these are static paths that
                must come before the dynamic /inbox/:inboxId routes so React
                Router matches them literally. The personal inbox email detail
                receives the email object via router state (no detail API). */}
            <Route path="/inbox/compose" element={<ProtectedRoute><PersonalComposePage /></ProtectedRoute>} />
            <Route path="/inbox/email/:commId" element={<ProtectedRoute><PersonalThreadView /></ProtectedRoute>} />
            {/* Full-page email detail and compose routes (Issue #244 — email
                rendering, threading, detail page, compose overhaul). The
                inbox list stays a `?inbox=` param; these sub-routes use path
                segments because they have their own URLs and back-button
                semantics. */}
            <Route path="/inbox/:inboxId/:conversationId" element={<ProtectedRoute><EmailDetailPage /></ProtectedRoute>} />
            <Route path="/inbox/:inboxId/compose" element={<ProtectedRoute><ComposePage /></ProtectedRoute>} />
            {/* Issue #662 — the manager-reachable per-inbox settings route.
                Guarded by InboxManagerRoute (never decides while the permission
                store is unknown — the cold-load trap), rendering the SAME
                InboxSettingsPage the mailbox-filter modal renders. React Router
                ranks static segments above params, so this wins over the
                two-param catch-all regardless of declaration order. */}
            <Route path="/inbox/:inboxId/settings" element={<InboxManagerRoute><InboxSettingsPage /></InboxManagerRoute>} />
            {/* Single page + mailbox switcher (Amendment 2) — deliberately no
                per-inbox route for READING. Which inbox is shown is a `?inbox=`
                param so a filtered view stays shareable, not a path segment.
                The one exception is the #662 settings surface just above, which
                needs its own URL so a cold load lands on it directly. */}
            <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
            {/* #680: the editor and the full event view are routes, not modals —
                a reload keeps the surface (the event is re-found from ?day=). */}
            <Route path="/calendar/new" element={<ProtectedRoute><CalendarEditorPage /></ProtectedRoute>} />
            <Route path="/calendar/event/:eventId" element={<ProtectedRoute><CalendarEventPage /></ProtectedRoute>} />
            <Route path="/calendar/event/:eventId/edit" element={<ProtectedRoute><CalendarEditorPage /></ProtectedRoute>} />
            {/* #675: /booking-links is the canonical home (Scheduling nav group).
                It used to redirect INTO a calendar tab; /calendar?tab=links now
                redirects here instead (handled in pages/Calendar.jsx). */}
            <Route path="/booking-links" element={<ProtectedRoute><BookingLinksPage /></ProtectedRoute>} />
            <Route path="/forms" element={<ProtectedRoute><Forms /></ProtectedRoute>} />
            <Route path="/forms/new/edit" element={<ProtectedRoute><FormBuilder /></ProtectedRoute>} />
            <Route path="/forms/:formId/workflow" element={<ProtectedRoute><WorkflowCanvas /></ProtectedRoute>} />
            <Route path="/forms/:formId/edit" element={<ProtectedRoute><FormBuilder /></ProtectedRoute>} />
            <Route path="/forms/:formId/steps/:stepId/design" element={<ProtectedRoute><CustomPageDesigner /></ProtectedRoute>} />
            <Route path="/forms/:formId/forwarders" element={<ProtectedRoute><ForwarderPage /></ProtectedRoute>} />
            <Route path="/forms/:formId/conditions/:nodeId" element={<ProtectedRoute><ConditionEditor /></ProtectedRoute>} />
            <Route path="/forms/:formId/data" element={<ProtectedRoute><FormDataPage /></ProtectedRoute>} />
            <Route path="/bookings" element={<Navigate to="/calendar" replace />} />
            <Route path="/ai-heartbeat" element={<FeatureRoute admin feature="ai_heartbeat"><AIHeartbeat /></FeatureRoute>} />
            <Route path="/ai-reviews" element={<FeatureRoute feature="agentic_personalization"><AgenticReviews /></FeatureRoute>} />
            <Route path="/feature-requests" element={<ProtectedRoute><FeatureRequests /></ProtectedRoute>} />
            <Route path="/release-notes" element={<ProtectedRoute><ReleaseNotes /></ProtectedRoute>} />
            <Route path="/content-library" element={<ProtectedRoute><ContentLibrary /></ProtectedRoute>} />
            {/* Meeting Copilot and Practice were extracted to module providers
                (prov_meeting_copilot, prov_practice) and their backends answer
                410. Nine routes here still mounted the PRE-extraction local UI,
                so a bookmark or typed URL rendered a page whose every request
                failed. The nav already hid them; the routes did not. */}
            <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
            <Route path="/wiki/*" element={<ProtectedRoute><WikiV2 /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><HelpGuide /></ProtectedRoute>} />
            <Route path="/testimonials" element={<ProtectedRoute><Testimonials /></ProtectedRoute>} />
            <Route path="/testimonials/new/edit" element={<ProtectedRoute><TestimonialBuilder /></ProtectedRoute>} />
            <Route path="/testimonials/:requestId/edit" element={<ProtectedRoute><TestimonialBuilder /></ProtectedRoute>} />
            <Route path="/communications" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsRedirect /></ProtectedRoute>} />
            <Route path="/settings/profile" element={<ProtectedRoute><SettingsProfilePage /></ProtectedRoute>} />
            <Route path="/settings/voice-capture" element={<ProtectedRoute><SettingsVoiceCapturePage /></ProtectedRoute>} />
            <Route path="/settings/integrations" element={<ProtectedRoute><SettingsIntegrationsPage /></ProtectedRoute>} />
            <Route path="/settings/ai-assistant" element={<ProtectedRoute><SettingsAIAssistantPage /></ProtectedRoute>} />
            <Route path="/settings/security" element={<ProtectedRoute><SettingsSecurityPage /></ProtectedRoute>} />
            {/* #955 — every signed-in role manages and shares their own templates and signatures. */}
            <Route path="/settings/templates-signatures" element={<ProtectedRoute><SettingsTemplatesSignaturesPage /></ProtectedRoute>} />
            {/* Module-iframe routes that share a prefix with CRM Core paths.
                When the Sites module is active, these render RuntimeShell
                (which shows the module iframe) instead of the native page. */}
            <Route path="/settings/team" element={<ProtectedRoute><RuntimeShell /></ProtectedRoute>} />
            <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
            <Route path="/products/:productId" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />
            <Route path="/email-sequences" element={<ProtectedRoute><EmailSequences /></ProtectedRoute>} />
            <Route path="/email-sequences/:sequenceId/edit" element={<ProtectedRoute><SequenceEditor /></ProtectedRoute>} />
            <Route path="/email-sequences/:sequenceId/monitor" element={<ProtectedRoute><SequenceMonitor /></ProtectedRoute>} />
            <Route path="/campaigns" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
            <Route path="/campaigns/:campaignId/edit" element={<ProtectedRoute><CampaignBuilder /></ProtectedRoute>} />
            <Route path="/campaigns/:campaignId/execution" element={<ProtectedRoute><CampaignExecutionReport /></ProtectedRoute>} />
            <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
            <Route path="/events/:eventId" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
            <Route path="/sites" element={<ProtectedRoute><RuntimeShell /></ProtectedRoute>} />
            <Route path="/sites/*" element={<ProtectedRoute><RuntimeShell /></ProtectedRoute>} />
            {/* Custom modules (plan Task 7.9). ONE route serves every installed module:
                its pages are the module's own internal routes, and the shell has no
                business enumerating them. Rendered as ProtectedRoute children so the
                rail, sidebar, TopBar and ⌘K stay live — a module page is meant to be
                indistinguishable from a Core page (D9), which it cannot be if it replaces
                the shell. The `/m/` prefix is short and reserved: it cannot collide with a
                Core path, and manifests declare it as `frontend.base_path`. */}
            <Route path="/m/:slug" element={<ProtectedRoute><ModuleHost /></ProtectedRoute>} />
            <Route path="/m/:slug/*" element={<ProtectedRoute><ModuleHost /></ProtectedRoute>} />
            {/* ATS / Recruitment */}
            <Route path="/recruitment" element={<ProtectedRoute><ATSDashboard /></ProtectedRoute>} />
            <Route path="/recruitment/jobs" element={<ProtectedRoute><ATSDashboard /></ProtectedRoute>} />
            <Route path="/recruitment/jobs/:jobId" element={<ProtectedRoute><ATSJobDetail /></ProtectedRoute>} />
            <Route path="/recruitment/talent-pool" element={<ProtectedRoute><ATSTalentPool /></ProtectedRoute>} />
            <Route path="/recruitment/agent-queue" element={<ProtectedRoute><ATSAgentQueue /></ProtectedRoute>} />
            <Route path="/recruitment/cv-review" element={<ProtectedRoute><ATSCVReview /></ProtectedRoute>} />
            <Route path="/recruitment/talent-pool/:applicantId" element={<ProtectedRoute><ATSApplicantDetail /></ProtectedRoute>} />
            <Route path="/recruitment/settings" element={<ProtectedRoute><ATSSettings /></ProtectedRoute>} />
            <Route path="/recruitment/reports" element={<ProtectedRoute><ATSReports /></ProtectedRoute>} />
            <Route path="/recruitment/activation" element={<ProtectedRoute><ATSActivationCampaigns /></ProtectedRoute>} />
            <Route path="/recruitment/matching" element={<ProtectedRoute><ATSMatchingWorkspace /></ProtectedRoute>} />
            {/* `agentDeveloper` is a nav-only restriction in workspaces.js; the
                routes were reachable by any authenticated user. The server-side
                gate is `require_permission('agents:read'|'agents:build')`. */}
            <Route path="/agents" element={<FeatureRoute feature="ai_assistant"><Agents /></FeatureRoute>} />
            <Route path="/agents/:agentId" element={<FeatureRoute feature="ai_assistant"><AgentBuilder /></FeatureRoute>} />
            {/* Sales Meeting Copilot */}
            <Route path="/playbooks" element={<AdminRoute><PlaybookList /></AdminRoute>} />
            <Route path="/meeting-sessions" element={<ProtectedRoute><MeetingSessions /></ProtectedRoute>} />
            {/* Practice / Role Play */}
            <Route path="/admin/role-play/avatars" element={<AdminRoute><AvatarLibrary /></AdminRoute>} />
            <Route path="/desktop-app" element={<ProtectedRoute><DesktopApp /></ProtectedRoute>} />
            {/* Module Provider Shell — installed third-party module iframes */}
            <Route path="/modules" element={<ProtectedRoute><ModulesPage /></ProtectedRoute>} />
            <Route path="/modules/:providerId" element={<ProtectedRoute><ModulesPage /></ProtectedRoute>} />
            <Route path="/list-management" element={<FeatureRoute admin feature="list_management"><ListManagement /></FeatureRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminRedirect /></AdminRoute>} />
            <Route path="/admin/organization" element={<AdminRoute><AdminOrganizationPage /></AdminRoute>} />
            <Route path="/admin/pending-approvals" element={<AdminRoute><AdminPendingApprovalsPage /></AdminRoute>} />
            <Route path="/admin/coaches" element={<AdminRoute><AdminCoachesPage /></AdminRoute>} />
            <Route path="/admin/user-groups" element={<AdminRoute><AdminUserGroupsPage /></AdminRoute>} />
            <Route path="/admin/deal-room-templates" element={<AdminRoute><AdminDealRoomTemplatesPage /></AdminRoute>} />
            <Route path="/admin/email-templates" element={<AdminRoute><AdminEmailTemplatesPage /></AdminRoute>} />
            <Route path="/admin/signatures" element={<AdminRoute><AdminSignaturesPage /></AdminRoute>} />
            <Route path="/admin/ai-skills" element={<AdminRoute><AdminAISkillsPage /></AdminRoute>} />
            <Route path="/admin/media-library" element={<AdminRoute><AdminMediaLibraryPage /></AdminRoute>} />
            <Route path="/admin/voice-profiles" element={<AdminRoute><AdminVoiceProfilesPage /></AdminRoute>} />
            <Route path="/admin/tags" element={<AdminRoute><AdminTagsPage /></AdminRoute>} />
            <Route path="/admin/developer-api" element={<AdminRoute><AdminDeveloperApiPage /></AdminRoute>} />
            <Route path="/admin/developer-api/keys/:keyId" element={<AdminRoute><AdminDeveloperApiKeyPage /></AdminRoute>} />
            <Route path="/admin/custom-fields" element={<AdminRoute><AdminCustomFieldsPage /></AdminRoute>} />
            <Route path="/admin/lead-pipeline" element={<AdminRoute><AdminLeadPipelinePage /></AdminRoute>} />
            <Route path="/admin/notifications" element={<AdminRoute><AdminNotificationsPage /></AdminRoute>} />
            <Route path="/admin/pipelines" element={<AdminRoute><AdminPipelinesPage /></AdminRoute>} />
            <Route path="/admin/event-types" element={<AdminRoute><AdminEventTypesPage /></AdminRoute>} />
            <Route path="/admin/product-settings" element={<AdminRoute><AdminProductSettingsPage /></AdminRoute>} />
            <Route path="/admin/app-launcher" element={<AdminRoute><AdminAppLauncherPage /></AdminRoute>} />
            <Route path="/admin/ai-usage" element={<AdminRoute><AdminAIUsagePage /></AdminRoute>} />
            <Route path="/admin/unified-inbox" element={<AdminRoute><AdminUnifiedInboxPage /></AdminRoute>} />
            <Route path="/admin/pitchbook-sync" element={<AdminRoute><AdminPitchBookSyncPage /></AdminRoute>} />
            <Route path="/admin/plan-usage" element={<AdminRoute><AdminPlanUsagePage /></AdminRoute>} />
            <Route path="/admin/modules" element={<AdminRoute><AdminModulesPage /></AdminRoute>} />
            <Route path="/admin/duplicate-contacts" element={<AdminRoute><AdminDuplicateContactsPage /></AdminRoute>} />
            {/* Legacy /super-admin redirect removed — route no longer exists */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 2 * 60 * 1000,      // 2 min — data considered fresh
            gcTime: 10 * 60 * 1000,         // 10 min — cache retained after unmount
            refetchOnWindowFocus: true,
            retry: 1,
        },
    },
});

function App() {
    // #904 — the one place task writes invalidate queries: every write helper in
    // lib/tasksApi dispatches `ncrm:tasks-changed`, and this is its only listener.
    useEffect(() => bindTaskQueries(queryClient), []);

    useEffect(() => {
        const remove = () => {
            // Remove Emergent badge by ID
            const badge = document.getElementById('emergent-badge');
            if (badge) badge.remove();
            // Remove any Emergent-injected scripts
            document.querySelectorAll('script[src*="emergent"]').forEach(el => el.remove());
            // Remove any Emergent-injected iframes or widgets
            document.querySelectorAll('iframe[src*="emergent"], div[id*="emergent"], div[class*="emergent"]').forEach(el => el.remove());
            // Remove any fixed-position widget injected at bottom-right (common pattern for third-party badges)
            document.querySelectorAll('a[href*="emergent"], a[href*="emergentagent"]').forEach(el => el.remove());
        };
        remove();
        // Also watch for it being re-injected
        const observer = new MutationObserver(remove);
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <AuthProvider>
                    <ThemeProvider>
                        <PermissionsProvider>
                            <PlanProvider>
                                <WorkspaceProvider>
                                    <PromptForgeBridge>
                                        <AppRouter />
                                    </PromptForgeBridge>
                                    <PlanErrorInterceptor />
                                    <StarFeatureModal />
                                    {/* <FloatingAI />  — disabled 2026-04-23; see BACKLOG AIO-01 */}
                                    <ToasterBoundary><ThemedToaster /></ToasterBoundary>
                                </WorkspaceProvider>
                            </PlanProvider>
                        </PermissionsProvider>
                    </ThemeProvider>
                </AuthProvider>
            </BrowserRouter>
            {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
    );
}

function ThemedToaster() {
    const { theme } = useTheme();
    return <Toaster position="top-right" richColors theme={theme === 'graphite' ? 'dark' : 'light'} />;
}

export default App;
